import React, { useState } from 'react';
import {
  Settings as SettingsIcon,
  Bell,
  Lock,
  User,
  Globe,
  Database,
  Mail,
  Shield,
  Monitor,
  Save,
  AlertTriangle,
  Check,
  X,
  Moon,
  Sun,
  Volume2,
  VolumeX,
  Eye,
  EyeOff,
  RefreshCw
} from 'lucide-react';

export const Settings: React.FC = () => {
  const [activeTab, setActiveTab] = useState('general');
  const [darkMode, setDarkMode] = useState(false);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [refreshInterval, setRefreshInterval] = useState(30);
  const [soundEnabled, setSoundEnabled] = useState(true);
  const [showSaveSuccess, setShowSaveSuccess] = useState(false);
  const [showSaveError, setShowSaveError] = useState(false);
  const [loading, setLoading] = useState(false);

  const tabs = [
    { id: 'general', name: 'General', icon: SettingsIcon },
    { id: 'notifications', name: 'Notifications', icon: Bell },
    { id: 'security', name: 'Security', icon: Shield },
    { id: 'system', name: 'System', icon: Monitor },
    { id: 'historian', name: 'Historian Data', icon: Database }
  ];

  const handleSave = async () => {
    setLoading(true);
    try {
      await new Promise(resolve => setTimeout(resolve, 1000));
      setShowSaveSuccess(true);
      setTimeout(() => setShowSaveSuccess(false), 3000);
    } catch (error) {
      setShowSaveError(true);
      setTimeout(() => setShowSaveError(false), 3000);
    }
    setLoading(false);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-lg shadow-lg p-6">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center space-x-3">
            <SettingsIcon className="h-6 w-6 text-gray-600" />
            <h2 className="text-xl font-bold text-gray-900">Settings</h2>
          </div>
          <div className="flex items-center space-x-4">
            <button
              onClick={handleSave}
              disabled={loading}
              className={`flex items-center space-x-2 px-4 py-2 rounded-lg ${
                loading
                  ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                  : 'bg-blue-600 text-white hover:bg-blue-700'
              }`}
            >
              <Save className="h-4 w-4" />
              <span>{loading ? 'Saving...' : 'Save Changes'}</span>
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="border-b border-gray-200">
          <nav className="-mb-px flex space-x-8">
            {tabs.map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center space-x-2 py-4 px-1 border-b-2 font-medium text-sm ${
                  activeTab === tab.id
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <tab.icon className="h-5 w-5" />
                <span>{tab.name}</span>
              </button>
            ))}
          </nav>
        </div>

        {/* Content */}
        <div className="mt-6">
          {activeTab === 'general' && (
            <div className="space-y-6">
              <div className="grid grid-cols-1 gap-6">
                <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                  <div className="flex items-center space-x-3">
                    {darkMode ? <Moon className="h-5 w-5" /> : <Sun className="h-5 w-5" />}
                    <div>
                      <h3 className="text-sm font-medium text-gray-900">Dark Mode</h3>
                      <p className="text-sm text-gray-500">Toggle dark mode theme</p>
                    </div>
                  </div>
                  <button
                    onClick={() => setDarkMode(!darkMode)}
                    className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${
                      darkMode ? 'bg-blue-600' : 'bg-gray-200'
                    }`}
                  >
                    <span className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                      darkMode ? 'translate-x-5' : 'translate-x-0'
                    }`} />
                  </button>
                </div>

                <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                  <div className="flex items-center space-x-3">
                    {soundEnabled ? <Volume2 className="h-5 w-5" /> : <VolumeX className="h-5 w-5" />}
                    <div>
                      <h3 className="text-sm font-medium text-gray-900">Sound Notifications</h3>
                      <p className="text-sm text-gray-500">Enable sound for alerts</p>
                    </div>
                  </div>
                  <button
                    onClick={() => setSoundEnabled(!soundEnabled)}
                    className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${
                      soundEnabled ? 'bg-blue-600' : 'bg-gray-200'
                    }`}
                  >
                    <span className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                      soundEnabled ? 'translate-x-5' : 'translate-x-0'
                    }`} />
                  </button>
                </div>

                <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                  <div className="flex items-center space-x-3">
                    <RefreshCw className="h-5 w-5" />
                    <div>
                      <h3 className="text-sm font-medium text-gray-900">Auto Refresh</h3>
                      <p className="text-sm text-gray-500">Automatically refresh data</p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-4">
                    <select
                      value={refreshInterval}
                      onChange={(e) => setRefreshInterval(Number(e.target.value))}
                      className="rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                    >
                      <option value={15}>15 seconds</option>
                      <option value={30}>30 seconds</option>
                      <option value={60}>1 minute</option>
                      <option value={300}>5 minutes</option>
                    </select>
                    <button
                      onClick={() => setAutoRefresh(!autoRefresh)}
                      className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${
                        autoRefresh ? 'bg-blue-600' : 'bg-gray-200'
                      }`}
                    >
                      <span className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                        autoRefresh ? 'translate-x-5' : 'translate-x-0'
                      }`} />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'historian' && (
            <div className="space-y-6">
              <div className="bg-white rounded-lg shadow p-6">
                <h3 className="text-lg font-medium text-gray-900 mb-4">Sample Historian Data</h3>
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Timestamp</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Tag Name</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Value</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Unit</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Quality</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {[
                        {
                          timestamp: '2024-03-14 10:00:00',
                          tagName: 'RW_FLOW_01',
                          description: 'Raw Water Flow',
                          value: 245.8,
                          unit: 'm³/h',
                          quality: 'Good'
                        },
                        {
                          timestamp: '2024-03-14 10:00:00',
                          tagName: 'RW_TURB_01',
                          description: 'Raw Water Turbidity',
                          value: 12.5,
                          unit: 'NTU',
                          quality: 'Good'
                        },
                        {
                          timestamp: '2024-03-14 10:00:00',
                          tagName: 'RW_PH_01',
                          description: 'Raw Water pH',
                          value: 7.2,
                          unit: 'pH',
                          quality: 'Good'
                        },
                        {
                          timestamp: '2024-03-14 10:00:00',
                          tagName: 'COAG_DOSE_01',
                          description: 'Coagulant Dosing Rate',
                          value: 42.5,
                          unit: 'L/h',
                          quality: 'Good'
                        },
                        {
                          timestamp: '2024-03-14 10:00:00',
                          tagName: 'FLOC_SPD_01',
                          description: 'Flocculator Speed',
                          value: 35,
                          unit: 'RPM',
                          quality: 'Good'
                        },
                        {
                          timestamp: '2024-03-14 10:00:00',
                          tagName: 'UF_TMP_01',
                          description: 'UF Trans-membrane Pressure',
                          value: 0.85,
                          unit: 'bar',
                          quality: 'Good'
                        },
                        {
                          timestamp: '2024-03-14 10:00:00',
                          tagName: 'UF_FLUX_01',
                          description: 'UF Flux Rate',
                          value: 65.2,
                          unit: 'LMH',
                          quality: 'Good'
                        },
                        {
                          timestamp: '2024-03-14 10:00:00',
                          tagName: 'RO_FEED_COND_01',
                          description: 'RO Feed Conductivity',
                          value: 520,
                          unit: 'µS/cm',
                          quality: 'Good'
                        },
                        {
                          timestamp: '2024-03-14 10:00:00',
                          tagName: 'RO_PERM_FLOW_01',
                          description: 'RO Permeate Flow',
                          value: 85.5,
                          unit: 'm³/h',
                          quality: 'Good'
                        },
                        {
                          timestamp: '2024-03-14 10:00:00',
                          tagName: 'RO_RECOVERY_01',
                          description: 'RO Recovery Rate',
                          value: 75.8,
                          unit: '%',
                          quality: 'Good'
                        },
                        {
                          timestamp: '2024-03-14 10:00:00',
                          tagName: 'RO_SEC1_PRESS',
                          description: 'RO Section 1 Pressure',
                          value: 15.2,
                          unit: 'bar',
                          quality: 'Good'
                        },
                        {
                          timestamp: '2024-03-14 10:00:00',
                          tagName: 'PROD_COND_01',
                          description: 'Product Water Conductivity',
                          value: 12.5,
                          unit: 'µS/cm',
                          quality: 'Good'
                        },
                        {
                          timestamp: '2024-03-14 10:00:00',
                          tagName: 'PROD_PH_01',
                          description: 'Product Water pH',
                          value: 6.8,
                          unit: 'pH',
                          quality: 'Good'
                        },
                        {
                          timestamp: '2024-03-14 10:00:00',
                          tagName: 'CL2_RESIDUAL_01',
                          description: 'Chlorine Residual',
                          value: 0.85,
                          unit: 'mg/L',
                          quality: 'Good'
                        },
                        {
                          timestamp: '2024-03-14 10:00:00',
                          tagName: 'STORAGE_LEVEL_01',
                          description: 'Storage Tank Level',
                          value: 82.5,
                          unit: '%',
                          quality: 'Good'
                        }
                      ].map((record, index) => (
                        <tr key={index} className="hover:bg-gray-50">
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {record.timestamp}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div>
                              <div className="text-sm font-medium text-gray-900">{record.tagName}</div>
                              <div className="text-sm text-gray-500">{record.description}</div>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {record.value}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {record.unit}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className="px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800">
                              {record.quality}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Success Toast */}
      {showSaveSuccess && (
        <div className="fixed bottom-4 right-4 flex items-center space-x-2 bg-green-50 p-4 rounded-lg border border-green-200">
          <Check className="h-5 w-5 text-green-500" />
          <span className="text-green-700">Settings saved successfully</span>
        </div>
      )}

      {/* Error Toast */}
      {showSaveError && (
        <div className="fixed bottom-4 right-4 flex items-center space-x-2 bg-red-50 p-4 rounded-lg border border-red-200">
          <AlertTriangle className="h-5 w-5 text-red-500" />
          <span className="text-red-700">Failed to save settings</span>
        </div>
      )}
    </div>
  );
};